import { useState, useEffect, useCallback } from "react";
import { Brain, BookOpen, Lightbulb, Radio, RefreshCw, AlertCircle, CheckCircle, XCircle, Activity, Shield, Eye, Zap, TrendingUp } from "lucide-react";

import { API_BASE as API } from "../config";

// Minimal markdown renderer — the Archivist lessons use a simple,
// controlled format (## headings, - bullets, **bold**). A real lib
// (react-markdown) is overkill for ~20 lessons/week of known shape.
function renderMarkdown(body) {
  if (!body) return null;
  const lines = body.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc ml-5 space-y-1 text-sm text-zinc-300">
          {listBuf.map((t, i) => <li key={i}>{renderInline(t)}</li>)}
        </ul>
      );
      listBuf = [];
    }
  };
  lines.forEach((line, i) => {
    if (/^##\s+/.test(line)) {
      flushList();
      out.push(<h3 key={i} className="text-sm font-mono font-bold text-teal-400 mt-4 mb-2 uppercase tracking-wider">{line.replace(/^##\s+/, "")}</h3>);
    } else if (/^-\s+/.test(line)) {
      listBuf.push(line.replace(/^-\s+/, ""));
    } else if (line.trim() === "") {
      flushList();
    } else {
      flushList();
      out.push(<p key={i} className="text-sm text-zinc-300 leading-relaxed mb-2">{renderInline(line)}</p>);
    }
  });
  flushList();
  return out;
}

function renderInline(text) {
  // Only **bold** for now; add more if lessons ever need it.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    /^\*\*.+\*\*$/.test(p)
      ? <strong key={i} className="text-white font-semibold">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Metric card with traffic-light styling based on healthy range.
// value null ⇒ greyed out; value in range ⇒ emerald; outside ⇒ red.
function MetricCard({ label, value, pct, healthyRange, sub, hint }) {
  let color = "text-zinc-400";
  let display = "—";
  if (value != null) {
    if (pct) display = `${Math.round(value * 100)}%`;
    else display = String(value);
    if (healthyRange) {
      const [lo, hi] = healthyRange;
      if (value >= lo && value <= hi) color = "text-emerald-400";
      else color = "text-red-400";
    } else {
      color = "text-zinc-100";
    }
  }
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3" title={hint}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-xl font-mono font-bold ${color}`}>{display}</div>
      {sub && <div className="text-[10px] text-zinc-600 font-mono">{sub}</div>}
    </div>
  );
}

export default function SkynetBrainTab({ token }) {
  const [status, setStatus] = useState(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [candidateLessons, setCandidateLessons] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [approved, setApproved] = useState([]);
  const [smartMoney, setSmartMoney] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [evolutionAlerts, setEvolutionAlerts] = useState([]);
  const [backtestReports, setBacktestReports] = useState([]);
  const [expandedReport, setExpandedReport] = useState(null); // filename when open
  const [reportBody, setReportBody] = useState({}); // filename → body cache
  const [runtimeConfig, setRuntimeConfig] = useState(null);
  const [fleetGuard, setFleetGuard] = useState(null);
  const [futuresStatus, setFuturesStatus] = useState(null);
  const [cleanPnl, setCleanPnl] = useState(null);
  const [cleanPnlShadow, setCleanPnlShadow] = useState(null);
  const [healthAudit, setHealthAudit] = useState(null);
  const [auditRunning, setAuditRunning] = useState(false);
  const [agentRuns, setAgentRuns] = useState({}); // { researcher: {running, tail, run_id, ...} }
  const [agentStarting, setAgentStarting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null); // filename currently being applied/rejected

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [rs, rl, rcl, rp, ra, rsm, rd, rv, rm, re, rcp, rbr, rrc, rfg, rfu] = await Promise.allSettled([
        fetch(`${API}/api/skynet-brain/status`, { headers }),
        fetch(`${API}/api/skynet-brain/latest-lesson`, { headers }),
        fetch(`${API}/api/skynet-brain/candidate-lessons`, { headers }),
        fetch(`${API}/api/skynet-brain/proposals`, { headers }),
        fetch(`${API}/api/skynet-brain/proposals-approved`, { headers }),
        fetch(`${API}/api/skynet-brain/smart-money`, { headers }),
        fetch(`${API}/api/skynet-brain/deployments`, { headers }),
        fetch(`${API}/api/skynet-brain/supervisor-reviews`, { headers }),
        fetch(`${API}/api/skynet-brain/metrics`, { headers }),
        fetch(`${API}/api/skynet-brain/evolution-alerts`, { headers }),
        fetch(`${API}/api/skynet-brain/clean-pnl`, { headers }),
        fetch(`${API}/api/skynet-brain/backtest-reports`, { headers }),
        fetch(`${API}/api/skynet-brain/runtime-config`, { headers }),
        fetch(`${API}/api/skynet-brain/fleet-guard`, { headers }),
        fetch(`${API}/api/skynet-brain/futures-status`, { headers }),
      ]);
      if (rs.status === "fulfilled" && rs.value.ok) setStatus(await rs.value.json());
      if (rl.status === "fulfilled" && rl.value.ok) setLesson(await rl.value.json());
      if (rcl.status === "fulfilled" && rcl.value.ok) {
        const data = await rcl.value.json();
        setCandidateLessons(data.candidates || []);
      }
      if (rp.status === "fulfilled" && rp.value.ok) {
        const data = await rp.value.json();
        setProposals(data.proposals || []);
      }
      if (ra.status === "fulfilled" && ra.value.ok) {
        const data = await ra.value.json();
        setApproved(data.proposals || []);
      }
      if (rsm.status === "fulfilled" && rsm.value.ok) setSmartMoney(await rsm.value.json());
      if (rd.status === "fulfilled" && rd.value.ok) {
        const data = await rd.value.json();
        setDeployments(data.deployments || []);
      }
      if (rv.status === "fulfilled" && rv.value.ok) {
        const data = await rv.value.json();
        setReviews(data.reviews || []);
      }
      if (rm.status === "fulfilled" && rm.value.ok) setMetrics(await rm.value.json());
      if (re.status === "fulfilled" && re.value.ok) {
        const data = await re.value.json();
        setEvolutionAlerts(data.alerts || []);
      }
      if (rcp.status === "fulfilled" && rcp.value.ok) {
        setCleanPnl(await rcp.value.json());
      }
      // Shadow P&L — separate fetch to avoid shifting the allSettled indices
      try {
        const rshadow = await fetch(`${API}/api/skynet-brain/clean-pnl-shadow`, { headers });
        if (rshadow.ok) setCleanPnlShadow(await rshadow.json());
      } catch (e) { /* shadow endpoint optional, swallow */ }
      if (rbr.status === "fulfilled" && rbr.value.ok) {
        const data = await rbr.value.json();
        setBacktestReports(data.reports || []);
      }
      if (rrc.status === "fulfilled" && rrc.value.ok) {
        setRuntimeConfig(await rrc.value.json());
      }
      if (rfg.status === "fulfilled" && rfg.value.ok) {
        setFleetGuard(await rfg.value.json());
      }
      if (rfu.status === "fulfilled" && rfu.value.ok) {
        setFuturesStatus(await rfu.value.json());
      }
    } catch {}
    setLoading(false);
  }, [token]);

  const decide = useCallback(async (filename, action) => {
    if (!window.confirm(`${action === "apply" ? "APPLY to live fleet queue" : "REJECT"}: ${filename}?`)) return;
    setActingOn(filename);
    try {
      const r = await fetch(`${API}/api/skynet-brain/proposals-approved/${encodeURIComponent(filename)}/${action}`, {
        method: "POST", headers,
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok !== false) {
        await fetchAll();
      } else {
        alert(`Action failed: ${d.detail || d.reason || r.statusText}`);
      }
    } catch (e) {
      alert(`Action failed: ${e.message}`);
    }
    setActingOn(null);
  }, [token, fetchAll]);

  // Commit 32: spawn a single agent on demand + poll status
  const AGENTS = ["archivist", "researcher", "backtester", "proposal_consumer", "money_printer", "safe_manager", "supervisor"];

  const runAgent = useCallback(async (name) => {
    if (!token || agentStarting) return;
    setAgentStarting(name);
    try {
      const r = await fetch(`${API}/api/skynet-brain/run-agent/${name}`, { method: "POST", headers });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.ok === false) {
        alert(`${name} failed to start: ${d.reason || r.statusText}`);
      } else {
        // Seed the state so the status strip shows "running" immediately
        setAgentRuns((prev) => ({ ...prev, [name]: { running: true, tail: [], run_id: d.run_id, started_at: d.started_at } }));
      }
    } catch (e) {
      alert(`${name}: ${e.message}`);
    }
    setAgentStarting(null);
  }, [token, agentStarting]);

  const pollAgentStatus = useCallback(async (name) => {
    try {
      const r = await fetch(`${API}/api/skynet-brain/run-agent/${name}/status`, { headers });
      if (r.ok) {
        const d = await r.json();
        setAgentRuns((prev) => ({ ...prev, [name]: d }));
      }
    } catch {}
  }, [token]);

  // Poll running agents every 3s
  useEffect(() => {
    const interval = setInterval(() => {
      AGENTS.forEach((a) => {
        if (agentRuns[a]?.running) pollAgentStatus(a);
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [agentRuns, pollAgentStatus]);

  // On mount, fetch current status of each agent once so we know if any are already running
  useEffect(() => {
    if (!token) return;
    AGENTS.forEach((a) => pollAgentStatus(a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Commit 39: load a backtest report's full body on expand
  const loadReport = useCallback(async (filename) => {
    if (reportBody[filename]) {
      setExpandedReport(expandedReport === filename ? null : filename);
      return;
    }
    try {
      const r = await fetch(`${API}/api/skynet-brain/backtest-reports/${encodeURIComponent(filename)}`, { headers });
      if (r.ok) {
        const d = await r.json();
        setReportBody((prev) => ({ ...prev, [filename]: d.body || "(empty)" }));
        setExpandedReport(filename);
      }
    } catch {}
  }, [token, reportBody, expandedReport]);

  // Commit 34: apply an approved proposal directly to runtime config
  const applyToRuntime = useCallback(async (filename) => {
    if (!token || actingOn) return;
    setActingOn(filename);
    try {
      // Step 1: preview — show what will change
      const pr = await fetch(`${API}/api/skynet-brain/apply-proposal/${encodeURIComponent(filename)}/preview`, { headers });
      const pd = await pr.json();
      if (!pr.ok || pd.ok === false) {
        alert(`Preview failed: ${pd.reason || pr.statusText}`);
        setActingOn(null);
        return;
      }
      // Build a human-readable diff summary
      const diffLines = Object.entries(pd.diff_vs_current || {}).map(([k, v]) =>
        `  ${k}: ${v.before === undefined ? "(unset)" : v.before} → ${v.after}`
      );
      const opsLines = (pd.ops_report || []).map(
        (o) => `  [${o.supported ? "✓" : "✗"}] ${o.alias} → ${o.runtime_key || "?"} = ${o.value}  (${o.reason})`
      );
      if (!diffLines.length) {
        alert(`No effective changes.\n\nOps report:\n${opsLines.join("\n") || "(none)"}`);
        setActingOn(null);
        return;
      }
      const confirmed = window.confirm(
        `Apply runtime config update?\n\n` +
        `Hypothesis: ${pd.proposal_hypothesis || "(none)"}\n` +
        `Confidence: ${pd.proposal_confidence || "?"}\n\n` +
        `Diff:\n${diffLines.join("\n")}\n\n` +
        `Ops:\n${opsLines.join("\n")}\n\n` +
        `The live multi_dca_bot reads this file every tick. Takes effect within seconds.`
      );
      if (!confirmed) {
        setActingOn(null);
        return;
      }
      // Step 2: actually apply
      const r = await fetch(`${API}/api/skynet-brain/apply-proposal/${encodeURIComponent(filename)}`, { method: "POST", headers });
      const d = await r.json();
      if (r.ok && d.ok) {
        alert(`Applied.\n\nRuntime config now:\n${JSON.stringify(d.runtime_config_after, null, 2)}`);
        await fetchAll();
      } else {
        alert(`Apply failed: ${d.reason || r.statusText}`);
      }
    } catch (e) {
      alert(`Error: ${e.message}`);
    }
    setActingOn(null);
  }, [token, actingOn, fetchAll]);

  // Commit 30: autodebugger — on-demand health probe of dashboard endpoints
  const runHealthAudit = useCallback(async () => {
    if (!token || auditRunning) return;
    setAuditRunning(true);
    try {
      const r = await fetch(`${API}/api/skynet-brain/health-audit`, { headers });
      if (r.ok) setHealthAudit(await r.json());
      else setHealthAudit({ error: `HTTP ${r.status}` });
    } catch (e) {
      setHealthAudit({ error: e.message });
    }
    setAuditRunning(false);
  }, [token, auditRunning]);

  // Commit 21: promote/reject candidate lessons
  const decideCandidate = useCallback(async (filename, action) => {
    const verb = action === "promote" ? "PROMOTE to canon" : "REJECT";
    if (!window.confirm(`${verb}: ${filename}?`)) return;
    setActingOn(filename);
    try {
      const r = await fetch(`${API}/api/skynet-brain/candidate-lessons/${encodeURIComponent(filename)}/${action}`, {
        method: "POST", headers,
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok !== false) {
        await fetchAll();
      } else {
        alert(`Action failed: ${d.detail || d.reason || r.statusText}`);
      }
    } catch (e) {
      alert(`Action failed: ${e.message}`);
    }
    setActingOn(null);
  }, [token, fetchAll]);

  useEffect(() => { fetchAll(); const iv = setInterval(fetchAll, 30000); return () => clearInterval(iv); }, [fetchAll]);

  if (loading && !status) {
    return <div className="text-center py-20 text-zinc-600 font-mono text-sm">Loading SKYNET brain state…</div>;
  }

  const memoryMissing = status && !status.memory_dir_exists;

  return (
    <div className="space-y-6" data-testid="skynet-brain-tab">
      {/* Header strip */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-teal-400" />
          <h2 className="text-base font-mono font-bold text-zinc-100">SKYNET Brain</h2>
          <span className="text-xs text-zinc-600 font-mono hidden md:inline">what the agents have been thinking</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runHealthAudit}
            disabled={auditRunning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono text-amber-400 border border-amber-700/60 rounded-lg hover:bg-amber-950/40 transition-all disabled:opacity-40"
            title="Probe every dashboard endpoint, show which are 500-ing and why"
          >
            <Activity className="w-3 h-3" /> {auditRunning ? "Auditing…" : "Auto-Debug"}
          </button>
          <button
            onClick={fetchAll}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono text-zinc-400 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-all"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Auto-debugger results */}
      {healthAudit && !healthAudit.error && (
        <div className={`border rounded-xl p-4 ${healthAudit.summary?.broken > 0 ? "bg-red-950/20 border-red-500/40" : "bg-emerald-950/20 border-emerald-500/40"}`}>
          <div className="flex items-center gap-2 mb-2">
            <Activity className={`w-4 h-4 ${healthAudit.summary?.broken > 0 ? "text-red-400" : "text-emerald-400"}`} />
            <h3 className={`text-sm font-mono font-bold ${healthAudit.summary?.broken > 0 ? "text-red-400" : "text-emerald-400"}`}>
              Auto-Debug Report
            </h3>
            <span className="text-[10px] text-zinc-500 font-mono">
              {healthAudit.summary?.ok}/{healthAudit.summary?.total} healthy · probed {new Date(healthAudit.checked_at).toLocaleTimeString()}
            </span>
          </div>
          {healthAudit.summary?.broken > 0 ? (
            <div className="space-y-2">
              {healthAudit.broken.map((b, i) => (
                <div key={i} className="bg-zinc-950/60 border border-red-500/20 rounded p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-red-500/20 text-red-300 rounded">
                      {b.status || "ERR"}
                    </span>
                    <code className="text-xs font-mono text-zinc-300">{b.path}</code>
                  </div>
                  {b.snippet && (
                    <div className="text-[10px] font-mono text-zinc-500 break-all mb-1" style={{maxHeight: "60px", overflow: "auto"}}>
                      {b.snippet}
                    </div>
                  )}
                  {b.last_logs && b.last_logs.length > 0 && (
                    <details className="text-[10px] font-mono text-zinc-600">
                      <summary className="cursor-pointer hover:text-zinc-400">recent log hits ({b.last_logs.length})</summary>
                      <div className="mt-1 pl-2 border-l border-zinc-700 space-y-0.5">
                        {b.last_logs.map((l, j) => <div key={j} className="break-all">{l}</div>)}
                      </div>
                    </details>
                  )}
                </div>
              ))}
              {healthAudit.recent_asgi_exceptions?.length > 0 && (
                <details className="mt-2 text-[10px] font-mono text-zinc-500">
                  <summary className="cursor-pointer text-amber-400 hover:text-amber-300">Most recent ASGI exceptions</summary>
                  <div className="mt-1 pl-2 border-l border-amber-700 space-y-0.5">
                    {healthAudit.recent_asgi_exceptions.map((l, i) => <div key={i} className="break-all">{l}</div>)}
                  </div>
                </details>
              )}
            </div>
          ) : (
            <div className="text-xs font-mono text-emerald-400">All {healthAudit.summary?.total} endpoints healthy.</div>
          )}
        </div>
      )}
      {healthAudit?.error && (
        <div className="bg-red-950/30 border border-red-500/40 rounded-xl p-3 text-xs font-mono text-red-300">
          Auto-debug failed: {healthAudit.error}
        </div>
      )}

      {/* Agent launcher — one button per agent, with live tail */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-mono font-bold text-purple-400">Run Agent</h3>
          <span className="text-[10px] text-zinc-600 font-mono">
            click to spawn on server · tail streams every 3s
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {AGENTS.map((name) => {
            const st = agentRuns[name];
            const running = st?.running;
            const label = name.replace(/_/g, " ");
            return (
              <div key={name} className="bg-zinc-950/60 border border-zinc-800 rounded p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-zinc-300 capitalize">{label}</span>
                  {running ? (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded animate-pulse">
                      running
                    </span>
                  ) : st?.run_id ? (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded">
                      idle
                    </span>
                  ) : (
                    <span className="text-[10px] font-mono text-zinc-600">never run</span>
                  )}
                </div>
                <button
                  onClick={() => runAgent(name)}
                  disabled={running || agentStarting === name}
                  className="w-full text-[11px] font-mono px-2 py-1 border border-purple-700/60 text-purple-300 rounded hover:bg-purple-950/40 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {agentStarting === name ? "Starting…" : running ? "…" : "Run"}
                </button>
                {st?.tail?.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[10px] font-mono text-zinc-500 cursor-pointer hover:text-zinc-300">
                      tail ({st.tail.length} lines)
                    </summary>
                    <pre className="mt-1 text-[9px] font-mono text-zinc-400 bg-black/40 rounded p-1 max-h-32 overflow-auto whitespace-pre-wrap break-all">
                      {st.tail.slice(-30).join("\n")}
                    </pre>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {memoryMissing && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs font-mono text-amber-300 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Memory directory not found at <code className="text-amber-200">{status?.memory_dir}</code>.
            Run the Archivist / Researcher agents on this box (or rsync the Mac memory dir here) to populate it.
          </div>
        </div>
      )}

      {/* Status counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Lessons</div>
          <div className="text-xl font-mono font-bold text-teal-400">{status?.lessons_count ?? 0}</div>
          <div className="text-[10px] text-zinc-600 font-mono">last {timeAgo(status?.latest_lesson_at)}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Proposals</div>
          <div className="text-xl font-mono font-bold text-amber-400">{status?.proposals_count ?? 0}</div>
          <div className="text-[10px] text-zinc-600 font-mono">last {timeAgo(status?.latest_proposal_at)}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Smart Money</div>
          <div className="text-xl font-mono font-bold text-purple-400">{status?.smart_money_traders ?? "—"}</div>
          <div className="text-[10px] text-zinc-600 font-mono">{timeAgo(status?.smart_money_as_of)}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Memory Dir</div>
          <div className="text-[10px] font-mono text-zinc-400 break-all leading-tight mt-1" title={status?.memory_dir}>
            {status?.memory_dir?.replace("/root/skynet-app/", "")
              || "—"}
          </div>
        </div>
      </div>

      {/* CLEAN P&L — TRUTH VIEW (active live bots only, NO ghost contamination) */}
      {cleanPnl && cleanPnl.daily && cleanPnl.daily.length > 0 && (() => {
        const { daily, cumulative, portfolio, initial_capital, current_value,
                total_pnl, total_deals, best_day, worst_day, date_range } = cleanPnl;
        // Portfolio-value chart — y axis starts at initial capital, grows over time
        const chartW = 720;
        const chartH = 180;
        const padL = 50, padR = 10, padT = 10, padB = 24;
        const innerW = chartW - padL - padR;
        const innerH = chartH - padT - padB;
        // Use portfolio value if available, otherwise fall back to cumulative+initial
        const initCap = initial_capital ?? 4640;
        const series = (portfolio && portfolio.length > 0) ? portfolio : cumulative.map(c => ({
          date: c.date, value: initCap + c.pnl, realized: c.pnl, unrealized: 0
        }));
        const valMax = Math.max(...series.map(s => s.value), initCap);
        const valMin = Math.min(...series.map(s => s.value), initCap);
        // Pad y axis so growth is visible (10% headroom both sides)
        const valSpan = Math.max(valMax - valMin, initCap * 0.005);
        const yMin = valMin - valSpan * 0.15;
        const yMax = valMax + valSpan * 0.25;
        const ySpan = yMax - yMin;
        const xStep = series.length > 1 ? innerW / (series.length - 1) : 0;
        const pathPoints = series.map((s, i) => {
          const x = padL + i * xStep;
          const y = padT + innerH - ((s.value - yMin) / ySpan) * innerH;
          return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
        }).join(" ");
        // Area-fill path: same line, then drop to bottom-right and bottom-left
        const lastX = padL + (series.length - 1) * xStep;
        const baseY = padT + innerH;
        const areaPath = pathPoints + ` L ${lastX.toFixed(1)} ${baseY.toFixed(1)} L ${padL.toFixed(1)} ${baseY.toFixed(1)} Z`;
        // Initial capital reference line
        const initY = padT + innerH - ((initCap - yMin) / ySpan) * innerH;
        // Daily bars (positive green, negative red)
        const dailyMax = Math.max(...daily.map(d => Math.abs(d.pnl)), 1);
        const barW = Math.max(innerW / daily.length - 2, 3);
        const curValue = current_value ?? series[series.length - 1]?.value ?? initCap;
        const portfolioGrowth = curValue - initCap;
        const portfolioGrowthPct = initCap > 0 ? (portfolioGrowth / initCap) * 100 : 0;

        return (
          <div className="bg-zinc-900/40 border-2 border-emerald-500/30 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-mono font-bold text-emerald-400">
                Portfolio Growth — Live Fleet
              </h3>
              <span className="text-[10px] text-zinc-600 font-mono">
                ghost/archived bots excluded · MongoDB source of truth · post-restructure
              </span>
            </div>
            <div className="text-[11px] text-zinc-500 font-mono italic mb-3">
              Total fleet value (realized + unrealized) since {cleanPnl.since}.
              Initial deployment: ${initCap.toFixed(0)} across {(cleanPnl.fleet || []).length} bots.
            </div>

            {/* Summary strip — now portfolio-value-centric */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
                <div className="text-[10px] uppercase text-zinc-500">Portfolio Value</div>
                <div className={`text-xl font-mono font-bold ${portfolioGrowth >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  ${curValue.toFixed(2)}
                </div>
                <div className="text-[9px] font-mono text-zinc-500">
                  init ${initCap.toFixed(0)}
                </div>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
                <div className="text-[10px] uppercase text-zinc-500">Growth</div>
                <div className={`text-xl font-mono font-bold ${portfolioGrowth >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {portfolioGrowth >= 0 ? "+" : ""}${portfolioGrowth.toFixed(2)}
                </div>
                <div className={`text-[9px] font-mono ${portfolioGrowthPct >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                  {portfolioGrowthPct >= 0 ? "+" : ""}{portfolioGrowthPct.toFixed(2)}%
                </div>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
                <div className="text-[10px] uppercase text-zinc-500">Realized</div>
                <div className={`text-sm font-mono font-bold ${total_pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {total_pnl >= 0 ? "+" : ""}${total_pnl.toFixed(2)}
                </div>
                <div className="text-[9px] font-mono text-zinc-500">
                  {total_deals} deals
                </div>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
                <div className="text-[10px] uppercase text-zinc-500">Best Day</div>
                <div className="text-sm font-mono font-bold text-emerald-400">
                  +${best_day?.pnl?.toFixed(2) ?? "—"}
                </div>
                <div className="text-[9px] text-zinc-600 font-mono">{best_day?.date}</div>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
                <div className="text-[10px] uppercase text-zinc-500">Worst Day</div>
                <div className={`text-sm font-mono font-bold ${(worst_day?.pnl ?? 0) < 0 ? "text-red-400" : "text-emerald-400"}`}>
                  {(worst_day?.pnl ?? 0) >= 0 ? "+" : ""}${worst_day?.pnl?.toFixed(2) ?? "—"}
                </div>
                <div className="text-[9px] text-zinc-600 font-mono">{worst_day?.date}</div>
              </div>
              <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
                <div className="text-[10px] uppercase text-zinc-500">Date Range</div>
                <div className="text-[11px] font-mono text-zinc-300 leading-tight mt-1">
                  {date_range?.from}<br />→ {date_range?.to}
                </div>
              </div>
            </div>

            {/* Portfolio Value chart — main growth view */}
            <div className="mb-4">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">
                Total Portfolio Value over time
                <span className="text-[9px] text-zinc-600 ml-2">(realized + unrealized · daily EOD snapshot)</span>
              </div>
              <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" preserveAspectRatio="xMidYMid meet">
                <rect x="0" y="0" width={chartW} height={chartH} fill="transparent" />
                {/* Initial capital reference line */}
                <line x1={padL} y1={initY} x2={chartW - padR} y2={initY} stroke="#52525b" strokeDasharray="3 3" />
                <text x={chartW - padR - 60} y={initY - 3} fill="#71717a" fontSize="9" fontFamily="monospace">
                  init ${initCap.toFixed(0)}
                </text>
                {/* Area fill */}
                <defs>
                  <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
                  </linearGradient>
                </defs>
                <path d={areaPath} fill="url(#portfolioGrad)" stroke="none" />
                {/* Line */}
                <path d={pathPoints} fill="none" stroke="#10b981" strokeWidth="2" />
                {/* Points — hover tooltip shows portfolio value, realized, unrealized */}
                {series.map((s, i) => {
                  const x = padL + i * xStep;
                  const y = padT + innerH - ((s.value - yMin) / ySpan) * innerH;
                  const isHover = hoverIdx === i;
                  return (
                    <g key={i}>
                      <circle
                        cx={x} cy={y} r="12"
                        fill="transparent"
                        style={{cursor:"pointer"}}
                        onMouseEnter={() => setHoverIdx(i)}
                        onMouseLeave={() => setHoverIdx(null)}
                      />
                      <circle cx={x} cy={y} r={isHover ? 5 : 3} fill={isHover ? "#34d399" : "#10b981"} pointerEvents="none" />
                    </g>
                  );
                })}
                {/* Y-axis labels */}
                <text x={5} y={padT + 6} fill="#a1a1aa" fontSize="10" fontFamily="monospace">${yMax.toFixed(0)}</text>
                <text x={5} y={padT + innerH + 4} fill="#a1a1aa" fontSize="10" fontFamily="monospace">${yMin.toFixed(0)}</text>
                {/* Hover tooltip */}
                {hoverIdx !== null && hoverIdx >= 0 && hoverIdx < series.length && (() => {
                  const s = series[hoverIdx];
                  const dEntry = daily[hoverIdx];
                  const x = padL + hoverIdx * xStep;
                  const y = padT + innerH - ((s.value - yMin) / ySpan) * innerH;
                  const tipW = 175;
                  const tipH = 84;
                  const tipX = x + tipW + 10 > chartW ? x - tipW - 10 : x + 10;
                  const tipY = Math.max(padT, Math.min(y - tipH / 2, chartH - tipH - padB));
                  const dailyPnl = dEntry?.pnl ?? 0;
                  const dailyDeals = dEntry?.deals ?? 0;
                  const growth = s.value - initCap;
                  return (
                    <g pointerEvents="none">
                      <line x1={x} y1={padT} x2={x} y2={padT + innerH} stroke="#10b981" strokeDasharray="2 2" opacity="0.4" />
                      <rect x={tipX} y={tipY} width={tipW} height={tipH} fill="#18181b" stroke="#10b981" strokeWidth="1" rx="4" />
                      <text x={tipX + 8} y={tipY + 14} fill="#a1a1aa" fontSize="10" fontFamily="monospace">{s.date}</text>
                      <text x={tipX + 8} y={tipY + 30} fill="#10b981" fontSize="12" fontFamily="monospace" fontWeight="bold">
                        Value: ${s.value.toFixed(2)}
                      </text>
                      <text x={tipX + 8} y={tipY + 44} fill={growth >= 0 ? "#34d399" : "#f87171"} fontSize="10" fontFamily="monospace">
                        {growth >= 0 ? "+" : ""}${growth.toFixed(2)} vs init
                      </text>
                      <text x={tipX + 8} y={tipY + 58} fill="#a1a1aa" fontSize="9" fontFamily="monospace">
                        Realized: {(s.realized ?? 0) >= 0 ? "+" : ""}${(s.realized ?? 0).toFixed(2)}  ·  Unreal: {(s.unrealized ?? 0) >= 0 ? "+" : ""}${(s.unrealized ?? 0).toFixed(2)}
                      </text>
                      <text x={tipX + 8} y={tipY + 74} fill={dailyPnl >= 0 ? "#34d399" : "#f87171"} fontSize="9" fontFamily="monospace">
                        Day PnL: {dailyPnl >= 0 ? "+" : ""}${dailyPnl.toFixed(2)} ({dailyDeals} deals)
                      </text>
                    </g>
                  );
                })()}
              </svg>
            </div>

            {/* Daily bars */}
            <div>
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Daily P&L (real money)</div>
              <div className="grid" style={{gridTemplateColumns: `repeat(${daily.length}, 1fr)`, gap: "2px", alignItems: "end", height: "80px"}}>
                {daily.map((d, i) => {
                  const height = Math.abs(d.pnl) / dailyMax * 70 + 4;
                  const isPos = d.pnl >= 0;
                  const isHover = hoverIdx === i;
                  return (
                    <div
                      key={i}
                      className="flex flex-col items-center relative"
                      onMouseEnter={() => setHoverIdx(i)}
                      onMouseLeave={() => setHoverIdx(null)}
                      style={{cursor: "pointer"}}
                    >
                      <div
                        className={isPos ? "bg-emerald-500/70 rounded-t" : "bg-red-500/70 rounded-t"}
                        style={{
                          height: `${height}px`,
                          width: "100%",
                          outline: isHover ? (isPos ? "1px solid #34d399" : "1px solid #f87171") : "none",
                          outlineOffset: "1px",
                        }}
                      />
                      <div className="text-[8px] font-mono text-zinc-600 mt-1" style={{fontSize: "8px", lineHeight: "10px"}}>
                        {d.date.slice(5)}
                      </div>
                      {isHover && (
                        <div
                          className="absolute z-10 bg-zinc-900 border border-emerald-500/50 rounded px-2 py-1 pointer-events-none"
                          style={{bottom: "100%", left: "50%", transform: "translateX(-50%)", marginBottom: "4px", whiteSpace: "nowrap"}}
                        >
                          <div className="text-[10px] font-mono text-zinc-400">{d.date}</div>
                          <div className={`text-xs font-mono font-bold ${isPos ? "text-emerald-400" : "text-red-400"}`}>
                            {isPos ? "+" : ""}${d.pnl.toFixed(2)}
                          </div>
                          <div className="text-[9px] font-mono text-zinc-500">{d.deals} deals</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="text-[10px] text-zinc-500 font-mono mt-3 italic">
              Excluded from this view (ghosts): {cleanPnl.archived_bot_ids_excluded?.join(", ") || "—"}
            </div>
          </div>
        );
      })()}

      {/* SHADOW P&L — paper-trading fleet (Commit 87) */}
      {cleanPnlShadow && (cleanPnlShadow.total_deals > 0 || (cleanPnlShadow.by_bot||[]).length > 0) && (
        <div className="bg-zinc-900/40 border-2 border-purple-500/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-mono font-bold text-purple-400">Shadow P&L — Paper Fleet</h3>
            <span className="text-[10px] text-zinc-600 font-mono">paper trades · not real money · graduation signal</span>
          </div>
          <div className="text-[11px] text-zinc-500 font-mono italic mb-3">
            15 shadow bots running DRY_RUN. Total real capital at risk: $0. Use this to rank which shadow deserves live promotion.
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] uppercase text-zinc-500">Total Paper P&L</div>
              <div className={`text-xl font-mono font-bold ${(cleanPnlShadow.total_pnl||0) >= 0 ? "text-purple-400" : "text-red-400"}`}>
                {(cleanPnlShadow.total_pnl||0) >= 0 ? "+" : ""}${(cleanPnlShadow.total_pnl||0).toFixed(2)}
              </div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] uppercase text-zinc-500">Paper Deals</div>
              <div className="text-xl font-mono font-bold text-zinc-100">{cleanPnlShadow.total_deals || 0}</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] uppercase text-zinc-500">Best Day</div>
              <div className="text-sm font-mono font-bold text-purple-400">+${cleanPnlShadow.best_day?.pnl?.toFixed(2) ?? "—"}</div>
              <div className="text-[9px] text-zinc-600 font-mono">{cleanPnlShadow.best_day?.date}</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] uppercase text-zinc-500">Worst Day</div>
              <div className={`text-sm font-mono font-bold ${(cleanPnlShadow.worst_day?.pnl ?? 0) < 0 ? "text-red-400" : "text-purple-400"}`}>
                {(cleanPnlShadow.worst_day?.pnl ?? 0) >= 0 ? "+" : ""}${cleanPnlShadow.worst_day?.pnl?.toFixed(2) ?? "—"}
              </div>
              <div className="text-[9px] text-zinc-600 font-mono">{cleanPnlShadow.worst_day?.date}</div>
            </div>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded p-2">
              <div className="text-[10px] uppercase text-zinc-500">Active Shadows</div>
              <div className="text-xl font-mono font-bold text-zinc-100">{(cleanPnlShadow.by_bot||[]).length}</div>
              <div className="text-[9px] text-zinc-600 font-mono">{cleanPnlShadow.date_range?.from} → {cleanPnlShadow.date_range?.to}</div>
            </div>
          </div>
          {/* Per-bot ranking — the graduation signal */}
          <div className="mt-4">
            <div className="text-[10px] uppercase text-zinc-500 mb-2">Per-Bot Ranking (graduation candidates)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono">
                <thead className="text-zinc-500">
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-1 px-2">Bot</th>
                    <th className="text-left py-1 px-2">Symbol</th>
                    <th className="text-right py-1 px-2" title="Closed deals PnL">Realized</th>
                    <th className="text-right py-1 px-2" title="Unrealized on current open position">Unreal</th>
                    <th className="text-right py-1 px-2" title="TRUE net: equity - capital">Net</th>
                    <th className="text-right py-1 px-2">Deals</th>
                    <th className="text-right py-1 px-2">WR</th>
                  </tr>
                </thead>
                <tbody>
                  {(cleanPnlShadow.by_bot||[]).map((b, i) => {
                    const realized = b.realized_pnl ?? b.pnl ?? 0;
                    const unreal = b.unrealized_pnl;
                    const net = b.net_pnl;
                    return (
                    <tr key={b.bot_id} className={`border-b border-zinc-900 ${i < 3 ? "bg-purple-950/20" : ""}`}>
                      <td className="py-1 px-2 text-zinc-300">{b.bot_id}</td>
                      <td className="py-1 px-2 text-zinc-500">{b.symbol}</td>
                      <td className={`py-1 px-2 text-right ${realized >= 0 ? "text-purple-400" : "text-red-400"}`}>
                        {realized >= 0 ? "+" : ""}${realized.toFixed(2)}
                      </td>
                      <td className={`py-1 px-2 text-right ${unreal === null || unreal === undefined ? "text-zinc-600" : unreal >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {unreal === null || unreal === undefined ? "—" : (unreal >= 0 ? "+" : "") + "$" + unreal.toFixed(2)}
                      </td>
                      <td className={`py-1 px-2 text-right font-bold ${net === null || net === undefined ? "text-zinc-600" : net >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {net === null || net === undefined ? "—" : (net >= 0 ? "+" : "") + "$" + net.toFixed(2)}
                      </td>
                      <td className="py-1 px-2 text-right text-zinc-400">{b.deals}</td>
                      <td className="py-1 px-2 text-right text-zinc-400">{b.win_rate}%</td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <div className="text-[10px] text-zinc-500 font-mono mt-3 italic">
            {cleanPnlShadow.note || "Paper trades only. Shadow APY typically overstates live by 5-15% due to zero-slippage assumption."}
          </div>
        </div>
      )}

      {/* METRICS — drift detection */}
      {metrics && metrics.sample_sizes && (
        <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-mono font-bold text-amber-400">Agent Health Metrics</h3>
            <span className="text-[10px] text-zinc-600 font-mono">
              decisions={metrics.sample_sizes.decisions} ·
              lessons={metrics.sample_sizes.lessons} ·
              proposals={metrics.sample_sizes.proposals}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard
              label="Rubber-stamp rate"
              value={metrics.safe_manager?.rubber_stamp_rate}
              pct
              healthyRange={[0.15, 0.85]}
              sub={`${metrics.safe_manager?.approve_count ?? 0}A · ${metrics.safe_manager?.resize_count ?? 0}R · ${metrics.safe_manager?.reject_count ?? 0}✗`}
              hint="Safe Manager approve/total. Too high = rubber-stamp; too low = blocker."
            />
            <MetricCard
              label="MP vs SM disagreement"
              value={metrics.safe_manager?.disagreement_rate}
              pct
              healthyRange={[0.20, 0.80]}
              sub="(reject + resize) / total"
              hint="Too low (<10%) means Safe Manager is submissive."
            />
            <MetricCard
              label="Archivist novelty"
              value={metrics.novelty?.archivist}
              pct
              healthyRange={[0.50, 1.0]}
              sub={`${metrics.sample_sizes.lessons} lessons`}
              hint="Fraction of non-repeating lesson titles (jaccard 0.70)."
            />
            <MetricCard
              label="Researcher novelty"
              value={metrics.novelty?.researcher}
              pct
              healthyRange={[0.50, 1.0]}
              sub={`${metrics.sample_sizes.proposals} proposals`}
              hint="Fraction of non-repeating hypothesis strings."
            />
          </div>
          {metrics.flags && metrics.flags.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {metrics.flags.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-xs font-mono text-red-400 bg-red-500/5 border border-red-500/20 rounded px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CANDIDATE LESSONS — Archivist output pending your approval before becoming canon */}
      {candidateLessons.length > 0 && (
        <div className="bg-zinc-900/40 border-2 border-cyan-500/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-mono font-bold text-cyan-400">
              Candidate Lessons — awaiting your sign-off
            </h3>
            <span className="text-[10px] text-zinc-600 font-mono">{candidateLessons.length} pending</span>
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mb-3 italic">
            Archivist wrote these. They are NOT canon until you Promote.
            Until then, the Researcher reads them as weak signal only.
            Reject sends to audit; Promote makes it permanent memory.
          </div>
          <div className="space-y-3">
            {candidateLessons.map((c, i) => (
              <div key={i} className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
                <div className="flex items-baseline justify-between mb-2 pb-2 border-b border-cyan-500/10">
                  <div>
                    <div className="text-sm font-mono font-bold text-white">{c.meta?.name || c.filename}</div>
                    <div className="text-[10px] font-mono text-zinc-600 mt-0.5">
                      {c.filename} · {timeAgo(c.mtime)}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-zinc-300 font-mono whitespace-pre-wrap max-h-[280px] overflow-y-auto">
                  {c.body?.slice(0, 1800)}
                  {c.body?.length > 1800 && "…"}
                </div>
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-cyan-500/10">
                  <button
                    disabled={actingOn === c.filename}
                    onClick={() => decideCandidate(c.filename, "promote")}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/20 transition-all disabled:opacity-50"
                  >
                    <CheckCircle className="w-3 h-3" /> Promote to Canon
                  </button>
                  <button
                    disabled={actingOn === c.filename}
                    onClick={() => decideCandidate(c.filename, "reject")}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-all disabled:opacity-50"
                  >
                    <XCircle className="w-3 h-3" /> Reject
                  </button>
                  {actingOn === c.filename && (
                    <span className="text-[10px] text-zinc-500 font-mono">working...</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LATEST LESSON */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-teal-400" />
          <h3 className="text-sm font-mono font-bold text-teal-400">Latest Archivist Lesson</h3>
        </div>
        {!lesson?.found ? (
          <div className="text-xs text-zinc-500 font-mono py-6 text-center">
            {lesson?.reason || "No lessons yet — run the Archivist agent"}
          </div>
        ) : (
          <div>
            <div className="flex items-baseline justify-between mb-3 pb-3 border-b border-zinc-800">
              <div>
                <div className="text-sm font-mono font-bold text-white">{lesson.lesson.meta?.name || lesson.lesson.filename}</div>
                <div className="text-[10px] font-mono text-zinc-600 mt-0.5">
                  {lesson.lesson.filename} · {timeAgo(lesson.lesson.mtime)} · {Math.round(lesson.lesson.size / 1024 * 10) / 10}KB
                </div>
              </div>
              {lesson.lesson.meta?.author && (
                <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 border border-purple-500/20 rounded px-2 py-1">
                  {lesson.lesson.meta.author.split("/").pop()}
                </span>
              )}
            </div>
            <div>{renderMarkdown(lesson.lesson.body)}</div>
          </div>
        )}
      </div>

      {/* PROPOSALS */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-mono font-bold text-amber-400">Pending Researcher Proposals</h3>
          <span className="text-[10px] text-zinc-600 font-mono">{proposals.length} queued</span>
        </div>
        {proposals.length === 0 ? (
          <div className="text-xs text-zinc-500 font-mono py-6 text-center">
            No proposals queued. Run the Researcher agent to generate some.
          </div>
        ) : (
          <div className="space-y-3">
            {proposals.map((p, i) => (
              <div key={i} className="bg-zinc-800/30 border border-zinc-800 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-sm font-mono text-zinc-100 leading-snug flex-1">{p.hypothesis}</div>
                  <div className="flex-shrink-0 flex flex-col items-end text-[10px] font-mono">
                    {p.confidence != null && (
                      <span className={`px-2 py-0.5 rounded ${
                        p.confidence >= 0.6 ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        : p.confidence >= 0.4 ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                        : "bg-zinc-500/10 text-zinc-400 border border-zinc-500/20"
                      }`}>
                        conf {p.confidence.toFixed(2)}
                      </span>
                    )}
                    <span className="text-zinc-600 mt-1">{timeAgo(p.mtime)}</span>
                  </div>
                </div>
                {p.why_now && <div className="text-[11px] font-mono text-zinc-500 mb-2 italic">why_now: {p.why_now}</div>}
                {p.patch?.operations?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {p.patch.operations.map((op, j) => (
                      <code key={j} className="text-[10px] font-mono bg-zinc-900 border border-zinc-700 text-zinc-300 rounded px-1.5 py-0.5">
                        {op.op}: {op.indicator_name || op.alias || op.path || op.filter_name}
                        {op.value != null ? ` = ${op.value}` : ""}
                        {op.enabled != null ? ` = ${op.enabled}` : ""}
                      </code>
                    ))}
                  </div>
                )}
                {p.counterargument && (
                  <div className="text-[11px] font-mono text-red-400/70 mt-1">⚠ {p.counterargument}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* APPROVED PROPOSALS — awaiting human Apply/Reject */}
      {approved.length > 0 && (
        <div className="bg-zinc-900/40 border-2 border-emerald-500/30 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-mono font-bold text-emerald-400">
              Approved — awaiting your decision
            </h3>
            <span className="text-[10px] text-zinc-600 font-mono">{approved.length} ready</span>
          </div>
          <div className="space-y-3">
            {approved.map((p, i) => (
              <div key={i} className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-sm font-mono text-zinc-100 leading-snug flex-1">{p.hypothesis}</div>
                  <div className="flex-shrink-0 flex flex-col items-end text-[10px] font-mono">
                    {p.confidence != null && (
                      <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        conf {p.confidence.toFixed(2)}
                      </span>
                    )}
                    <span className="text-zinc-600 mt-1">{timeAgo(p.mtime)}</span>
                  </div>
                </div>
                {p.why_now && <div className="text-[11px] font-mono text-zinc-500 mb-2 italic">why_now: {p.why_now}</div>}
                {p.patch?.operations?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {p.patch.operations.map((op, j) => (
                      <code key={j} className="text-[10px] font-mono bg-zinc-900 border border-zinc-700 text-zinc-300 rounded px-1.5 py-0.5">
                        {op.op}: {op.indicator_name || op.alias || op.path || op.filter_name}
                        {op.value != null ? ` = ${op.value}` : ""}
                        {op.enabled != null ? ` = ${op.enabled}` : ""}
                      </code>
                    ))}
                  </div>
                )}
                {p.counterargument && (
                  <div className="text-[11px] font-mono text-red-400/70 mb-2">⚠ {p.counterargument}</div>
                )}
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-emerald-500/10 flex-wrap">
                  <button
                    disabled={actingOn === p.filename}
                    onClick={() => applyToRuntime(p.filename)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono bg-cyan-500/10 text-cyan-400 border border-cyan-500/40 rounded-lg hover:bg-cyan-500/20 transition-all disabled:opacity-50"
                    title="Preview the runtime config change, then apply"
                  >
                    <Zap className="w-3 h-3" /> Apply to Runtime
                  </button>
                  <button
                    disabled={actingOn === p.filename}
                    onClick={() => decide(p.filename, "apply")}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/20 transition-all disabled:opacity-50"
                  >
                    <CheckCircle className="w-3 h-3" /> Queue
                  </button>
                  <button
                    disabled={actingOn === p.filename}
                    onClick={() => decide(p.filename, "reject")}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-all disabled:opacity-50"
                  >
                    <XCircle className="w-3 h-3" /> Reject
                  </button>
                  {actingOn === p.filename && (
                    <span className="text-[10px] text-zinc-500 font-mono">working...</span>
                  )}
                </div>
                <div className="text-[9px] text-zinc-700 font-mono mt-2">{p.filename}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] text-zinc-600 font-mono italic">
            Apply moves the proposal to <code>queued-for-live/</code>. It does NOT directly push config to running bots —
            a separate applicator (Commit 13) reviews + pushes after a final audit.
          </div>
        </div>
      )}

      {/* FLEET GUARD STATUS — correlation-risk brake observability */}
      {fleetGuard && (
        <div className={`border rounded-xl p-4 ${fleetGuard.any_tripped ? "bg-red-950/20 border-red-500/40" : "bg-cyan-950/10 border-cyan-500/30"}`}>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Shield className={`w-4 h-4 ${fleetGuard.any_tripped ? "text-red-400" : "text-cyan-400"}`} />
            <h3 className={`text-sm font-mono font-bold ${fleetGuard.any_tripped ? "text-red-400" : "text-cyan-400"}`}>
              Fleet Guard {fleetGuard.any_tripped ? "— TRIPPED" : "— armed, passive"}
            </h3>
            <span className="text-[10px] text-zinc-600 font-mono">
              {fleetGuard.threshold_events_today || 0} threshold-block{(fleetGuard.threshold_events_today || 0) !== 1 ? "s" : ""} ·
              {" "}{fleetGuard.stale_events_today || 0} stale-block{(fleetGuard.stale_events_today || 0) !== 1 ? "s" : ""} today
              {fleetGuard.snapshot_age_sec != null && ` · snapshot ${Math.round(fleetGuard.snapshot_age_sec)}s`}
              {fleetGuard.snapshot_stale && <span className="text-red-400 ml-1">· STALE</span>}
            </span>
          </div>

          {/* Doctor self-health */}
          {fleetGuard.doctor_health?.cadence_known && (
            <div className="text-[10px] font-mono text-zinc-500 mb-2 flex flex-wrap gap-3">
              <span>Doctor last run: <span className="text-zinc-300">{Math.round(fleetGuard.doctor_health.last_run_age_sec)}s ago</span></span>
              <span>avg gap: <span className="text-zinc-300">{Math.round(fleetGuard.doctor_health.avg_gap_sec)}s</span></span>
              <span>max gap: <span className={fleetGuard.doctor_health.max_gap_sec > fleetGuard.doctor_health.expected_cadence_sec * 2 ? "text-amber-400" : "text-zinc-300"}>{Math.round(fleetGuard.doctor_health.max_gap_sec)}s</span></span>
              <span>expected: <span className="text-zinc-300">{fleetGuard.doctor_health.expected_cadence_sec}s</span></span>
            </div>
          )}

          {/* Gates table */}
          <div className="space-y-1.5 mb-3">
            {fleetGuard.gates.map((g, i) => {
              const statusColor = !g.armed ? "zinc" : g.tripped ? "red" : "emerald";
              return (
                <div key={i} className={`flex items-center gap-3 text-xs font-mono bg-zinc-950/40 border border-${statusColor}-500/20 rounded p-2`}>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded bg-${statusColor}-500/20 text-${statusColor}-300 whitespace-nowrap`}>
                    {!g.armed ? "off" : g.tripped ? "TRIP" : "ok"}
                  </span>
                  <code className="text-zinc-300 min-w-0 truncate">{g.name}</code>
                  <span className="text-zinc-500 text-[11px] whitespace-nowrap">
                    measured <code className={g.tripped ? "text-red-400" : "text-zinc-300"}>{g.measured ?? "?"}</code>
                    {" "}vs threshold <code className="text-zinc-400">{g.threshold ?? "—"}</code>
                  </span>
                  <span className="text-[10px] text-zinc-600 hidden md:inline ml-auto">{g.unit}</span>
                </div>
              );
            })}
            {fleetGuard.snapshot_stale && (
              <div className="text-xs font-mono bg-red-950/30 border border-red-500/30 rounded p-2 text-red-300">
                ⚠ Fleet Doctor snapshot stale (age {fleetGuard.snapshot_age_sec != null ? `${Math.round(fleetGuard.snapshot_age_sec)}s` : "unknown"} &gt; {fleetGuard.thresholds.fleet_stale_ceiling_sec}s ceiling).
                Live bots fail-closed until Doctor refreshes. Shadow bots proceed.
              </div>
            )}
          </div>

          {/* Recent block events */}
          {fleetGuard.recent_events && fleetGuard.recent_events.length > 0 && (
            <details>
              <summary className="text-[11px] font-mono text-zinc-400 cursor-pointer hover:text-zinc-200">
                Recent block events ({fleetGuard.recent_events.length} in last 24h)
              </summary>
              <div className="mt-2 space-y-1 max-h-64 overflow-auto">
                {fleetGuard.recent_events.slice(0, 30).map((ev, i) => {
                  const kindColor = ev.kind === "stale_snapshot" ? "red" : "amber";
                  return (
                  <div key={i} className={`text-[10px] font-mono bg-zinc-950/60 border border-${kindColor}-500/20 rounded p-1.5`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-zinc-500">{new Date(ev.ts).toLocaleTimeString()}</span>
                      <span className={`text-[9px] px-1 py-0.5 rounded bg-${kindColor}-500/20 text-${kindColor}-300`}>{ev.kind || "?"}</span>
                      <span className="text-amber-400">{ev.bot_id || "?"}</span>
                      <span className="text-zinc-400">{ev.symbol}</span>
                    </div>
                    <div className="pl-3 text-zinc-500">
                      {(ev.reasons || []).map((r, j) => <div key={j}>· {r}</div>)}
                    </div>
                  </div>
                )})}
              </div>
            </details>
          )}
          {(!fleetGuard.recent_events || fleetGuard.recent_events.length === 0) && (
            <div className="text-[10px] font-mono text-zinc-600 italic">
              No block events in the last 24h — guard has not fired yet.
            </div>
          )}
        </div>
      )}

      {/* FUTURES PANEL — shadow + live perps bots (Commit 56) */}
      {futuresStatus && futuresStatus.bot_count > 0 && (
        <div className={`border rounded-xl p-4 ${futuresStatus.kill_switch_active ? "bg-red-950/30 border-red-500/60" : "bg-purple-950/20 border-purple-500/30"}`}>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Zap className={`w-4 h-4 ${futuresStatus.kill_switch_active ? "text-red-400" : "text-purple-400"}`} />
            <h3 className={`text-sm font-mono font-bold ${futuresStatus.kill_switch_active ? "text-red-400" : "text-purple-400"}`}>
              Futures {futuresStatus.kill_switch_active ? "— KILL SWITCH ENGAGED" : ""}
            </h3>
            <span className="text-[10px] text-zinc-500 font-mono">
              {futuresStatus.bot_count} bot{futuresStatus.bot_count !== 1 ? "s" : ""} ·
              {" "}{futuresStatus.live_count} live · {futuresStatus.shadow_count} shadow
            </span>
          </div>

          {futuresStatus.kill_switch_active && (
            <div className="mb-3 p-2 bg-red-950/60 border border-red-500/50 rounded text-xs font-mono text-red-300">
              🛑 <b>All futures bots halted.</b> Reason: {futuresStatus.kill_switch_reason || "(unknown)"}.
              Delete <code>/root/.skynet/futures_disabled</code> to re-enable.
            </div>
          )}

          <div className="space-y-2">
            {futuresStatus.bots.map((b, i) => {
              const inDeal = b.in_deal;
              const isLive = !b.dry_run;
              const modeColor = isLive ? "emerald" : "zinc";
              const pnlColor = b.total_realized_pnl > 0 ? "text-emerald-400" : b.total_realized_pnl < 0 ? "text-red-400" : "text-zinc-400";
              return (
                <div key={i} className={`bg-zinc-950/60 border border-${modeColor}-500/30 rounded p-2`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded bg-${modeColor}-500/20 text-${modeColor}-300 font-mono`}>
                      {b.mode}
                    </span>
                    <span className="text-xs font-mono text-zinc-200">{b.symbol}</span>
                    <span className="text-[10px] text-zinc-500 font-mono">{b.bot_dir}</span>
                    {b.leverage_used > 0 && (
                      <span className="text-[10px] font-mono text-amber-400">{b.leverage_used}x lev</span>
                    )}
                    {isLive && !b.graduated_for_live && (
                      <span className="text-[10px] font-mono text-red-400">⚠ NOT GRADUATED</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
                    <div>
                      <div className="text-zinc-600 text-[9px] uppercase">Status</div>
                      <div className={inDeal ? "text-amber-400" : "text-zinc-400"}>
                        {inDeal ? "IN_DEAL" : "WAITING"}
                      </div>
                    </div>
                    <div>
                      <div className="text-zinc-600 text-[9px] uppercase">Deals</div>
                      <div className="text-zinc-300">
                        {b.deals_completed} ({b.deals_won}W)
                      </div>
                    </div>
                    <div>
                      <div className="text-zinc-600 text-[9px] uppercase">Realized PnL</div>
                      <div className={`${pnlColor} font-bold`}>
                        {b.total_realized_pnl >= 0 ? "+" : ""}${Number(b.total_realized_pnl).toFixed(4)}
                      </div>
                    </div>
                    <div>
                      <div className="text-zinc-600 text-[9px] uppercase">Avg / Stop</div>
                      <div className="text-zinc-300 text-[10px]">
                        {b.avg_entry ? `$${Number(b.avg_entry).toFixed(2)}` : "—"}
                        {b.stop_price ? ` → $${Number(b.stop_price).toFixed(2)}` : ""}
                      </div>
                    </div>
                  </div>
                  {inDeal && (
                    <div className="mt-2 text-[10px] font-mono text-zinc-500">
                      qty {Number(b.qty).toFixed(4)} · margin ${Number(b.invested_usdt).toFixed(2)} ·
                      {" "}opened {b.deal_start_time ? new Date(b.deal_start_time).toLocaleTimeString() : "?"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-3 text-[10px] text-zinc-600 font-mono">
            Shadow bots are paper only — no real capital. Live requires graduation marker:
            {" "}<code>/root/.skynet/futures_graduated/&lt;SYMBOL&gt;.graduated</code>
          </div>
        </div>
      )}

      {/* RUNTIME CONFIG — what the live bots are enforcing RIGHT NOW */}
      {runtimeConfig && (
        <div className={`border rounded-xl p-4 ${runtimeConfig.exists ? "bg-emerald-950/20 border-emerald-500/30" : "bg-zinc-900/40 border-zinc-800/60"}`}>
          <div className="flex items-center gap-2 mb-3">
            <Shield className={`w-4 h-4 ${runtimeConfig.exists ? "text-emerald-400" : "text-zinc-500"}`} />
            <h3 className={`text-sm font-mono font-bold ${runtimeConfig.exists ? "text-emerald-400" : "text-zinc-400"}`}>
              Active Runtime Overrides
            </h3>
            <span className="text-[10px] text-zinc-600 font-mono">
              read every tick by multi_dca_bot · source of truth
            </span>
          </div>
          {runtimeConfig.exists ? (
            Object.keys(runtimeConfig.config || {}).length > 0 ? (
              <div className="space-y-1">
                {Object.entries(runtimeConfig.config).map(([k, v]) => {
                  if (k === "per_bot") {
                    return (
                      <div key={k} className="text-[11px] font-mono">
                        <div className="text-zinc-500 mb-1">per_bot overrides:</div>
                        {Object.entries(v || {}).map(([bot, cfg]) => (
                          <div key={bot} className="pl-3 text-zinc-300">
                            <span className="text-amber-400">{bot}</span>: <code className="text-emerald-300">{JSON.stringify(cfg)}</code>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <div key={k} className="flex items-center gap-3 text-xs font-mono">
                      <code className="text-zinc-400">{k}</code>
                      <span className="text-zinc-600">=</span>
                      <code className="text-emerald-300 font-bold">{String(v)}</code>
                    </div>
                  );
                })}
                <div className="text-[9px] text-zinc-600 font-mono mt-2 break-all">{runtimeConfig.path}</div>
              </div>
            ) : (
              <div className="text-[11px] font-mono text-zinc-500 italic">
                File exists but empty — no overrides active. Env-var defaults apply (RSI_ENTRY_CEILING etc.).
              </div>
            )
          ) : (
            <div className="text-[11px] font-mono text-zinc-500 italic">
              No runtime config file yet. Env-var defaults apply. Click Apply to Runtime on any approved proposal to create one.
            </div>
          )}
        </div>
      )}

      {/* BACKTEST REPORTS — Backtester verdicts, newest first */}
      {backtestReports.length > 0 && (
        <div className="bg-zinc-900/40 border border-cyan-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-mono font-bold text-cyan-400">Backtest Reports</h3>
            <span className="text-[10px] text-zinc-600 font-mono">
              {backtestReports.length} report{backtestReports.length > 1 ? "s" : ""} · Backtester agent verdicts
            </span>
          </div>
          <div className="space-y-2">
            {backtestReports.map((r) => {
              const verdictColor = r.verdict === "GREEN" ? "emerald"
                                : r.verdict === "RED"   ? "red"
                                : "amber"; // YELLOW / ?
              const isOpen = expandedReport === r.filename;
              return (
                <div key={r.filename} className={`bg-zinc-950/60 border border-${verdictColor}-500/30 rounded p-2`}>
                  <div
                    className="flex items-start gap-2 cursor-pointer"
                    onClick={() => loadReport(r.filename)}
                  >
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 bg-${verdictColor}-500/20 text-${verdictColor}-300 rounded whitespace-nowrap`}>
                      {r.verdict}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-mono text-zinc-300 truncate">{r.proposal}</div>
                      <div className="text-[9px] text-zinc-600 font-mono">{r.created}</div>
                    </div>
                    <span className="text-[10px] text-zinc-500 font-mono">{isOpen ? "▼" : "▶"}</span>
                  </div>
                  {isOpen && reportBody[r.filename] && (
                    <pre className="mt-2 text-[10px] font-mono text-zinc-300 bg-black/40 rounded p-2 max-h-96 overflow-auto whitespace-pre-wrap break-all">
                      {reportBody[r.filename]}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DEPLOYMENT PIPELINE — Money Printer + Safe Manager + Applicator */}
      {deployments.length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-purple-400" />
            <h3 className="text-sm font-mono font-bold text-purple-400">Deployment Pipeline</h3>
            <span className="text-[10px] text-zinc-600 font-mono">{deployments.length} recent</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-zinc-500 uppercase border-b border-zinc-800">
                  <th className="text-left py-2 px-2">Bot</th>
                  <th className="text-left py-2 px-2">Sym</th>
                  <th className="text-right py-2 px-2">Req $</th>
                  <th className="text-center py-2 px-2">MP source</th>
                  <th className="text-center py-2 px-2">SM</th>
                  <th className="text-right py-2 px-2">Approved $</th>
                  <th className="text-center py-2 px-2">Live?</th>
                  <th className="text-right py-2 px-2">Age</th>
                </tr>
              </thead>
              <tbody>
                {deployments.slice(0, 20).map((d, i) => (
                  <tr key={i} className="border-b border-zinc-800/30">
                    <td className="py-2 px-2 text-zinc-200">{d.bot_id || "—"}</td>
                    <td className="py-2 px-2 text-zinc-400">{d.symbol || "—"}</td>
                    <td className="py-2 px-2 text-right text-zinc-300">
                      ${d.requested_capital_usd?.toFixed(0) ?? "—"}
                    </td>
                    <td className="py-2 px-2 text-center text-[10px] text-zinc-500">{d.source}</td>
                    <td className="py-2 px-2 text-center">
                      {d.decision_status === "approved" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">approved</span>
                      )}
                      {d.decision_status === "resized" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">resized</span>
                      )}
                      {d.decision_status === "rejected" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">rejected</span>
                      )}
                      {!d.decision_status && (
                        <span className="text-[10px] text-zinc-600">pending</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right text-zinc-300">
                      {d.decision_capital_usd != null ? `$${d.decision_capital_usd.toFixed(0)}` : "—"}
                    </td>
                    <td className="py-2 px-2 text-center">
                      {d.location === "applied" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">LIVE</span>
                      )}
                      {d.location === "rollback" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">rollback</span>
                      )}
                      {d.location === "pending" && (
                        <span className="text-[10px] text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right text-zinc-600">{timeAgo(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* EVOLUTION ALERTS — the Telegram 'EVOLUTION FOUND BETTER CONFIG' feed */}
      {evolutionAlerts.length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-pink-400" />
            <h3 className="text-sm font-mono font-bold text-pink-400">
              Evolution Alerts
            </h3>
            <span className="text-[10px] text-zinc-600 font-mono">
              {evolutionAlerts.length} events from auto_evolution.py
            </span>
          </div>
          <div className="text-[11px] text-zinc-500 font-mono italic mb-3">
            This is the same feed that posts 🧬 "EVOLUTION FOUND BETTER CONFIG"
            to your Telegram. Approve/Skip still happens via the Telegram buttons
            — the dashboard shows you the history + current status.
          </div>
          <div className="space-y-2">
            {evolutionAlerts.slice(0, 10).map((e, i) => {
              const improv = e.improvement_pct ?? 0;
              const sig = improv > 5;
              return (
                <div
                  key={i}
                  className={`border rounded-lg p-3 ${
                    e.deployed
                      ? "bg-emerald-500/5 border-emerald-500/20"
                      : sig
                      ? "bg-pink-500/5 border-pink-500/20"
                      : "bg-zinc-800/30 border-zinc-800"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 text-xs font-mono">
                      <span className={`px-2 py-0.5 rounded text-[10px] ${
                        e.deployed
                          ? "bg-emerald-500/20 text-emerald-400"
                          : sig
                          ? "bg-pink-500/20 text-pink-400"
                          : "bg-zinc-700/30 text-zinc-500"
                      }`}>
                        {e.deployed ? "DEPLOYED" : sig ? "PROPOSED" : "no-op"}
                      </span>
                      <span className={improv >= 0 ? "text-emerald-400" : "text-red-400"}>
                        {improv >= 0 ? "+" : ""}{improv.toFixed(1)}%
                      </span>
                      <span className="text-zinc-400">
                        score {e.current_score?.toFixed(3)} → {e.best_score?.toFixed(3)}
                      </span>
                    </div>
                    <span className="text-[10px] text-zinc-600 font-mono">{timeAgo(e.timestamp)}</span>
                  </div>
                  {e._coin_summary && (
                    <div className="flex flex-wrap gap-2 mt-2 text-[10px] font-mono">
                      {Object.entries(e._coin_summary).map(([coin, dna]) => (
                        <code key={coin} className="bg-zinc-900 border border-zinc-700 text-zinc-300 rounded px-1.5 py-0.5">
                          <b className="text-zinc-100">{coin}</b>{" "}
                          BO=${dna.bo?.toFixed(0)} SO=${dna.so?.toFixed(0)} TP={dna.tp_pct?.toFixed(2)}%
                          SOs={dna.sos} step={dna.step_pct?.toFixed(1)}%
                        </code>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SUPERVISOR REVIEWS */}
      {reviews.length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-mono font-bold text-cyan-400">Supervisor Reviews</h3>
            <span className="text-[10px] text-zinc-600 font-mono">{reviews.length} total</span>
          </div>
          <div className="space-y-3">
            {reviews.slice(0, 2).map((r, i) => (
              <div key={i} className="bg-zinc-800/30 border border-zinc-800 rounded-lg p-3">
                <div className="flex items-baseline justify-between mb-2">
                  <div className="text-sm font-mono font-bold text-white">{r.meta?.name || r.filename}</div>
                  <div className="text-[10px] text-zinc-600 font-mono">{timeAgo(r.mtime)}</div>
                </div>
                <div className="text-xs text-zinc-300 font-mono whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                  {r.body?.slice(0, 1500)}
                  {r.body?.length > 1500 && "…"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SMART MONEY */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Radio className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-mono font-bold text-purple-400">Hyperliquid Smart Money</h3>
          {smartMoney?.stale_minutes != null && (
            <span className={`text-[10px] font-mono ${
              smartMoney.stale_minutes > 30 ? "text-red-400" : "text-zinc-600"
            }`}>
              {smartMoney.stale_minutes}m old{smartMoney.stale_minutes > 30 ? " (stale)" : ""}
            </span>
          )}
        </div>
        {!smartMoney?.found ? (
          <div className="text-xs text-zinc-500 font-mono py-6 text-center">
            {smartMoney?.reason || "No snapshot yet — run the Researcher agent to populate"}
          </div>
        ) : (
          <div>
            <div className="text-[10px] font-mono text-zinc-600 mb-3">
              top {smartMoney.traders_included} traders · window={smartMoney.window}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-zinc-500 uppercase border-b border-zinc-800">
                    <th className="text-left py-2 px-2">Coin</th>
                    <th className="text-right py-2 px-2">Long</th>
                    <th className="text-right py-2 px-2">Short</th>
                    <th className="text-right py-2 px-2">Net $ (M)</th>
                    <th className="text-center py-2 px-2">Sentiment</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(smartMoney.by_coin || {}).slice(0, 15).map(([coin, slot]) => (
                    <tr key={coin} className="border-b border-zinc-800/30">
                      <td className="py-2 px-2 text-zinc-100 font-semibold">{coin}</td>
                      <td className="py-2 px-2 text-right text-emerald-400">{slot.long_count}</td>
                      <td className="py-2 px-2 text-right text-red-400">{slot.short_count}</td>
                      <td className={`py-2 px-2 text-right ${slot.net_notional_usd >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        {slot.net_notional_usd >= 0 ? "+" : ""}{(slot.net_notional_usd / 1e6).toFixed(2)}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className={`text-[10px] px-2 py-0.5 rounded ${
                          slot.net_sentiment === "long" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          : slot.net_sentiment === "short" ? "bg-red-500/10 text-red-400 border border-red-500/20"
                          : "bg-zinc-500/10 text-zinc-400 border border-zinc-500/20"
                        }`}>
                          {slot.net_sentiment}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
